import { Box } from './kit'
import { box, line, P, poly } from './projection'

/* ==========================================================================
   The screen-print head on the carriage (local x 0 = carriage x; it rides
   the frame's lift, `frame-dy`). Two blade holders run along the depth
   under a head plate, each on its own small cylinder: the aluminium flood
   bar on the left (`fl-dz`) — on the pool's side at home, so it drags the
   ink across on the flood stroke — and the silicone squeegee on the right
   (`sq-dz`). The squeegee strokes right to left with its top leading,
   leaning 10° and flexing back to about 16° under pressure (`flex`, scene
   CSS); its trailing face, tilted up toward the camera, is the blade the
   tracking shot follows, with the ink bead rolling ahead of its edge.
   ========================================================================== */

const PLATE = box({ x0: -58, x1: 58, z0: -6, z1: 24, h0: 214, h1: 226 })
const SQ_CYL = box({ x0: 16, x1: 40, z0: -2, z1: 14, h0: 178, h1: 214 })
const FL_CYL = box({ x0: -42, x1: -18, z0: -2, z1: 14, h0: 178, h1: 214 })
const SQ_HOLDER = box({ x0: 20, x1: 36, z0: 6, z1: 166, h0: 132, h1: 148 })
const FL_HOLDER = box({ x0: -40, x1: -20, z0: 6, z1: 166, h0: 128, h1: 142 })
const FL_FACE = poly([P(-28, 128, 22), P(-28, 128, 158), P(-28, 102, 158), P(-28, 102, 22)])
const FL_END = poly([P(-32, 128, 22), P(-28, 128, 22), P(-28, 102, 22), P(-32, 102, 22)])
const FL_EDGE = line([P(-32, 102, 22), P(-28, 102, 22), P(-28, 102, 158)])
const HOLDER_SCREWS = [
  [28, 30],
  [28, 142],
  [-30, 30],
  [-30, 142],
]
  .map(([x, z]) => P(x, x > 0 ? 148 : 142, z))
  .map(([x, y]) => `M${(x + 3).toFixed(1)} ${y.toFixed(1)}a3 1.6 0 1 0 -6 0a3 1.6 0 1 0 6 0`)
  .join('')

function Chrome({ x, y0, y1, w }) {
  return (
    <>
      <rect className="f-cB" x={x - w / 2} y={y0} width={w} height={y1 - y0} />
      <rect className="f-cA" x={x - w / 2} y={y0} width={w * 0.22} height={y1 - y0} />
      <rect className="f-cC" x={x + w * 0.15} y={y0} width={w * 0.35} height={y1 - y0} />
    </>
  )
}

/** Guide rods and piston rod of the carriage's Z cylinder (drawn behind it). */
export function HeadRods() {
  return (
    <g className="pp-head">
      <Chrome x={-28} y0={296} y1={470} w={12} />
      <Chrome x={28} y0={296} y1={470} w={12} />
      <Chrome x={0} y0={296} y1={470} w={10} />
    </g>
  )
}

/* The squeegee: local blade space is (depth z, distance s down from the
   clamp) for its trailing face, (thickness, s) for its front end; the scene
   CSS maps both with the lean and the flex. Silicone body, a lit line where
   it leaves the holder, the bevel in the core tone, a crisp wet edge. */
function Squeegee() {
  return (
    <g className="pp-sq">
      <rect className="f-cB" x="23.9" y="517" width="6" height="32" />
      <g className="pp-blade-face">
        <rect className="f-si" x="22" y="0" width="136" height="44" />
        <rect className="f-siS" x="22" y="30" width="136" height="9" />
        <rect className="f-siC" x="22" y="39" width="136" height="5" />
        <path className="l-rimLit" d="M22 1.2H158" />
        <path className="l-edge" d="M22 44H158" />
      </g>
      <g className="pp-blade-end">
        <path className="f-siL" d="M-9 0H0V44H-3Z" />
        <path className="l-edge" d="M-3 44H0" />
      </g>
      <Box geo={SQ_HOLDER} />
    </g>
  )
}

function FloodBar() {
  return (
    <g className="pp-fl">
      <rect className="f-cB" x="-34.1" y="517" width="6" height="38" />
      <path className="f-aS" d={FL_FACE} />
      <path className="f-aF" d={FL_END} />
      <path className="l-hi" d={FL_EDGE} />
      <Box geo={FL_HOLDER} />
    </g>
  )
}

/** The head: blades and holders, their cylinders, the head plate. */
export function Head() {
  return (
    <g className="pp-head">
      <Squeegee />
      <FloodBar />
      <Box geo={FL_CYL} />
      <Box geo={SQ_CYL} />
      <path className="l-aS pp-hair" d="M-36 488V519M-24 488V519M22 488V519M34 488V519" />
      <Box geo={PLATE} />
      <path className="f-gR pp-hair" d={`${HOLDER_SCREWS}M-44 483a3 3 0 1 0 6 0a3 3 0 1 0 -6 0M38 483a3 3 0 1 0 6 0a3 3 0 1 0 -6 0`} />
    </g>
  )
}
